import { useRef, useState } from 'react';

/**
 * Double-submit guard for buttons that trigger async work (checkout,
 * waitlist captures, admin saves...).
 *
 * Combines a synchronous `ref` (blocks the second click before React
 * re-renders) with a reactive `isSubmitting` state (drives the disabled
 * UI / spinner). Without the ref a fast double-click can fire two
 * submits before the button visually disables.
 *
 * Usage:
 * ```tsx
 * const { isSubmitting, guard } = useSubmitGuard();
 * const onClick = () => guard(async () => {
 *   await createCheckout();
 * });
 * ```
 *
 * The async fn passed to `guard` is awaited and any thrown error is
 * re-thrown after resetting the guard, so the caller can `try/catch`
 * around the `guard(...)` call when needed.
 */
export function useSubmitGuard() {
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function guard(fn: () => Promise<void>): Promise<void> {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await fn();
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  return { isSubmitting, guard };
}
